import { sdkRequest } from '@eventbrite/http';
import { setLanguage } from '@eventbrite/i18n';
import { addDimensions, trackAnalyticsEvent } from '@eventbrite/site-analytics';
import React, { useEffect, useState } from 'react';
import BoostBannerRoot from './components/Banner/BoostBannerRoot';
import BookmarkRoot from './components/Bookmark/BookmarkRoot';
import CreatorCollectionsRoot from './components/CreatorCollections/CreatorCollectionsRoot';
import { EnhancedListingComponents } from './components/EnhancedListingComponents/EnhancedListingComponents';
import EventMapRoot from './components/EventMap/EventMapRoot';
import { ExpiredEvents } from './components/ExpiredEvents/ExpiredEvents';
import { FollowOrganizerController } from './components/FollowOrganizer/FollowOrganizerController';
import { NotificationBar } from './components/NotificationBar/NotificationBar';
import { ReportThisEventRoot } from './components/ReportThisEvent/ReportThisEventRoot';
import RSVPRoot from './components/RSVP/RSVPRoot';
import { SearchTakeoverRoot } from './components/SearchTakeover/SearchTakeoverRoot';
import SeriesDatesDropdownRoot from './components/SeriesDatesDropdown/SeriesDatesDropdownRoot';
import ShareEventRoot from './components/ShareEvent/ShareEventRoot';
import { StickyNav } from './components/StickyNav/StickyNav';
import { Analytics } from './components/utils/analytics';
import { useLocalizedDates } from './components/utils/localizedDates';
import { EVENT_DETAILS_FEATURE_GATE } from './constants';
import { OrganizerContext, SettingsContext, UserContext } from './context';
import { NotificationsContextProvider } from './contexts';
import { checkGateFromServer } from './statsig';
import type {
    AppProps,
    CollectionResponse,
    EventCollectionsResponse,
} from './types';
import { GACodes } from './types';

const setUpDimensions = (props: AppProps): void => {
    const {
        app_name: appName,
        app_version: appVersion,
        event,
        gaSettings,
    } = props;
    const {
        activeScopeUserId,
        correlationId,
        guestPartnerId,
        userPartnerId: userId,
        guestPartnerId: guestId,
    } = gaSettings;
    const {
        id: eventId,
        ownerId: eventOwnerUserId,
        source: eventSource,
    } = event;

    const dimensions = {
        activeScopeUserId,
        appName,
        appVersion,
        correlationId,
        eventId,
        eventOwnerUserId,
        eventSource,
        experimentId: 'OldListing',
        guestId,
        guestPartnerId,
        userId,
    };

    addDimensions(dimensions);
};

const useCreatorCollections = (
    eventId: string,
    launchLinkToCreatorCollectionsOnListing: boolean,
    isExpiredEvent: boolean,
) => {
    const [collections, setCollections] = useState<
        CollectionResponse[] | undefined
    >(undefined);

    useEffect(() => {
        let isMounted = true;

        if (isExpiredEvent) {
            return;
        }

        if (
            launchLinkToCreatorCollectionsOnListing &&
            eventId !== '91286489347' &&
            eventId !== '72676745101'
        ) {
            sdkRequest(`/events/${eventId}/collections/public/`).then(
                (storedCollections: EventCollectionsResponse) => {
                    if (storedCollections && isMounted)
                        return setCollections(storedCollections.collections);
                },
            );
        } else {
            return setCollections([]);
        }
        return () => {
            isMounted = false;
        };
    }, [eventId, launchLinkToCreatorCollectionsOnListing, isExpiredEvent]);

    return collections;
};

export const App: React.FC<AppProps> = (props: AppProps) => {
    const {
        env: {
            localeInfo: { locale },
        },
        user: { isAuthenticated, id: userId },
        event: {
            childEvents,
            end,
            hideEndDate,
            hideStartDate,
            id: eventId,
            isOnlineEvent,
            isSeriesParent,
            name: eventName,
            start,
            url: eventUrl,
            isExpiredEvent = false,
        },
        settings: {
            featureFlags: {
                launchLinkToCreatorCollectionsOnListing = false,
                enable_ssr_for_header = false,
                enable_event_details_on_listings = false,
            } = {},
        },
        gaSettings,
        tracking,
        components: {
            remindMe: { isRemindedEvent = false },
            bookmark: { isBookmarkedEvent = false } = {},
            followOrganizer: { isGDPRCountry },
            contactOrganizer: { captchaKey },
        },
        organizer: {
            id: organizerId,
            name: organizerName,
            url: organizerUrl,
            profilePicture: organizerProfilePicture,
            description: organizerDescription,
            eventTitle: organizerEventTitle,
            orgFacebook: organizerFacebook,
            orgTwitter: organizerTwitter,
            orgWebsite: organizerWebsite,
        },
        statsig,
    } = props;

    setLanguage(locale);

    setUpDimensions(props);

    const modalContainer = React.useRef<HTMLDivElement | null>(null);
    const creatorCollections = useCreatorCollections(
        eventId,
        launchLinkToCreatorCollectionsOnListing,
        isExpiredEvent,
    );

    if (isExpiredEvent) {
        ExpiredEvents();
    }

    const isEventDetailsFeatureGateActive =
        enable_event_details_on_listings &&
        checkGateFromServer(EVENT_DETAILS_FEATURE_GATE, statsig);

    const shouldEnableStickyNav =
        !isExpiredEvent && !isEventDetailsFeatureGateActive;

    if (shouldEnableStickyNav) {
        StickyNav();
    }

    Analytics({ ...tracking, eventId });

    useLocalizedDates({
        end,
        hideEndDate,
        hideStartDate,
        isOnlineEvent,
        isSeriesParent,
        locale,
        start,
    });

    useEffect(() => {
        trackAnalyticsEvent({
            category: GACodes.GA_CATEGORY_LISTING,
            action: GACodes.GA_ACTION_APP_LOAD,
            label: props.user.canManageEvent
                ? GACodes.GA_LABEL_CAN_EDIT
                : GACodes.GA_LABEL_CANNOT_EDIT,
            dimensions: {
                experimentId3: isEventDetailsFeatureGateActive
                    ? 'eventdetails:newdesign'
                    : 'eventdetails:control',
            },
        });
    }, [props.user.canManageEvent, isEventDetailsFeatureGateActive]);

    return (
        <div data-spec="listing-page-app">
            <SettingsContext.Provider value={props.settings}>
                <UserContext.Provider value={props.user}>
                    <OrganizerContext.Provider value={props.organizer}>
                        <ShareEventRoot
                            {...props}
                            modalContainer={modalContainer}
                        />
                        <NotificationsContextProvider>
                            <FollowOrganizerController
                                orgFacebook={organizerFacebook}
                                orgTwitter={organizerTwitter}
                                orgWebsite={organizerWebsite}
                                organizerId={organizerId}
                                organizerName={organizerName}
                                organizerProfilePicture={
                                    organizerProfilePicture
                                }
                                organizerUrl={organizerUrl}
                                organizerDescription={organizerDescription}
                                organizerEventTitle={organizerEventTitle}
                                organizers={[organizerId]}
                                organizerData={{
                                    [organizerId]: {
                                        id: organizerId,
                                        name: organizerName,
                                        url: organizerUrl,
                                        profilePicture: organizerProfilePicture,
                                        followedByYou: false,
                                    },
                                }}
                                userId={userId && userId.toString()}
                                captchaKey={captchaKey}
                                isAuthenticated={isAuthenticated}
                                eventName={eventName}
                                eventId={eventId}
                                eventUrl={eventUrl}
                                isRemindedEvent={isRemindedEvent}
                                isGDPRCountry={isGDPRCountry}
                                gaSettings={gaSettings}
                                gaCategory={gaSettings.category}
                            />
                            <NotificationBar />
                            <BookmarkRoot
                                eventId={eventId}
                                isBookmarked={isBookmarkedEvent}
                                queryParams={location.search}
                            />
                        </NotificationsContextProvider>
                        <RSVPRoot eventId={eventId} />
                        <SeriesDatesDropdownRoot
                            childEvents={childEvents || []}
                            eventId={eventId}
                            isOnlineEvent={isOnlineEvent}
                        />
                        <ReportThisEventRoot {...props} />
                        {!enable_ssr_for_header && (
                            <SearchTakeoverRoot {...props} />
                        )}
                        {eventId !== '91286489347' &&
                            eventId !== '72676745101' && (
                                <CreatorCollectionsRoot {...props} />
                            )}
                        <EnhancedListingComponents
                            {...props}
                            creatorCollections={creatorCollections}
                        />
                        <div
                            id="share-modal-wrapper"
                            ref={modalContainer}
                        ></div>
                        <div id="listings-root__overlay"></div>
                        <EventMapRoot {...props} />
                        <BoostBannerRoot eventId={eventId} />
                    </OrganizerContext.Provider>
                </UserContext.Provider>
            </SettingsContext.Provider>
        </div>
    );
};
